import { useState, useEffect } from 'react';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { unwrapTenderOpeningList } from '../utils/tenderOpeningHelpers';
import { buildTenderOpeningListUrl } from './useTenderOpeningList';

// Status values match the TenderOpeningStatus filter on the list endpoint.
const SUMMARY_KEYS = [
  { key: 'total', status: '' },
  { key: 'open', status: '0' },
  { key: 'inProgress', status: '1' },
  { key: 'done', status: '2' },
];

// Counts per tender opening status for one procurement category. Uses the
// list endpoint with PageSize=1 and reads totalItems, so no extra API is needed.
const useTenderOpeningSummary = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  enabled = true,
}) => {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (!enabled || !token || !isAuthenticated()) return undefined;

    const controller = new AbortController();
    setIsLoading(true);

    Promise.all(
      SUMMARY_KEYS.map(async ({ key, status }) => {
        const url = buildTenderOpeningListUrl({
          currentPage: 1,
          rowsPerPage: 1,
          procurementTypeHeader,
          tenderOpeningStatus: status,
        });
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const { totalItems } = unwrapTenderOpeningList(await response.json());
        return [key, totalItems];
      })
    )
      .then(entries => setSummary(Object.fromEntries(entries)))
      .catch(error => {
        // The list hook already reports load failures; the cards just hide counts.
        if (error.name !== 'AbortError') setSummary(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [procurementTypeHeader, refreshTrigger, enabled]);

  return { summary, isLoading };
};

export default useTenderOpeningSummary;
